import { GetContentOptions } from '@builder.io/sdk';

import { excludedArticleIds } from '@/helpers/BuilderHelper';
import { Locale } from '@/helpers/LangHelper';
import { Article } from '@/types/Article';
import BaseModel from './BaseModel';
import CategoryModel from './CategoryModel';

class ArticleModel extends BaseModel<Article> {
    constructor() {
        super({ modelName: 'article' });
    }

    fromBuilderContent = (object: any): Article => {
        if (!object) return object;

        const oData = object.data;

        const categories = oData.categories?.map((category: any) => CategoryModel.fromBuilderContent(category.category.value));
        const author = oData?.author?.value?.data;

        const articleSlug = oData.url?.replace('/builder/', '') ?? '';

        const articleUrl = categories && categories.length ? `${categories[0].categoryUrl}/${articleSlug}`.replace(/\/{2,}/, '/') : null;

        return {
            id: object.id ?? null,
            title: oData.title ?? '',
            description: oData.description ?? '',
            categories: categories ?? null,
            author: author ?? null,
            contributors: oData.contributors ?? [],

            leadImage: oData.leadImage ?? null,
            leadPortraitImage: oData.leadPortraitImage ?? null,
            imageMobile: oData.imageMobile ?? null,
            position: oData.position ?? null,

            url: articleUrl ?? '',
            articleUrl: articleUrl ?? '',
            articleSlug,

            priority: oData.priority ?? 0,

            builderModel: object ?? null,

            seoTitle: oData.seoTitle ?? oData.title ?? '',
            seoDescription: oData.seoDescription ?? oData.description ?? '',

            leadVideo: oData.leadVideo ?? null,

            lastUpdated: object.lastUpdated ?? null,
        };
    };

    getArticle = async (path: string, locale: Locale, includeUnpublished?: boolean) => {
        const urlPath = '/builder/' + path;

        return this.builder
            .get(this.modelName, {
                options: {
                    locale,
                    includeUnpublished,
                },
                userAttributes: {
                    urlPath,
                    locale,
                },
            })
            .toPromise();
    };

    getRelatedArticles = async (article: Article, locale?: Locale) => {
        if (!article) {
            return [];
        }

        const categoryIds = article.categories
            ? [...article.categories].map((item) => {
                  return item.id;
              })
            : [];

        const config: GetContentOptions = {
            options: { noTargeting: true },
            query: {
                id: {
                    $nin: excludedArticleIds.concat([article.builderModel.id]),
                },
                'data.categories': {
                    $elemMatch: {
                        'category.id': {
                            $in: [categoryIds[0]],
                        },
                    },
                },
            },
            limit: 3,
        };
        if (locale) {
            config.query.query = {
                $elemMatch: {
                    property: {
                        $eq: 'locale',
                    },
                    value: locale,
                },
            };
        }

        const relatedPages = await this.builder.getAll(this.modelName, config);
        const relatedArticles: Article[] = relatedPages.map((page) => {
            return this.fromBuilderContent(page);
        });

        return relatedArticles;
    };

    getByCategories = ({ locale, limit, offset, ids, fields }: { locale: Locale; limit: number; offset?: number; ids: string[]; fields?: string }) => {
        return this.findAll({
            filters: {
                locale,
                limit,
                offset,
                extra: {
                    id: {
                        $nin: excludedArticleIds,
                    },
                    'data.categories': {
                        $elemMatch: {
                            'category.id': {
                                $in: ids,
                            },
                        },
                    },
                },
            },
            fields,
        });
    };
}

export default new ArticleModel();
